import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom';
import checkStatus, { readStatusWithRetries } from './jsFunctions/checkStatus.js'

import { Box, Button, CircularProgress, Typography } from '@mui/material';

import Heading from './components/Heading/Heading';
import MainTemplate from './components/MainTemplate/MainTemplate';

import URL2 from './config.js'
import {
    getCampusPaymentCharges,
    getCampusSessions,
    clearCampusPaymentCharges,
    markCampusChargePaid,
    clearPaymentResumeState,
    getPaymentResumeState,
} from './utils/campusPaymentCharges.js'
import { getCampusPaymentSummary, hasOutstandingCampusPayment, shouldRedirectToCompletion } from './utils/campusPaymentStatus.js';
import { cardPaymentSteps, stepsStillToPay } from './utils/campusPaymentSteps.js';
import { CampusPaymentDone } from './components/CampusPaymentSteps/CampusPaymentSteps.jsx';
import { useSelector } from 'react-redux';
import { getParentEmails } from './utils/parentHistoryHelpers.js';
import { queueConfirmationEmail } from './utils/helper-functions.js';


const Thankyou = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const hasSaved = useRef(false);
    // Shown only if the payment could not be saved; otherwise this screen moves straight on.
    const [saveError, setSaveError] = useState('');
    // A campus is paid but another still needs paying: what was paid, and what is next.
    const [nextUp, setNextUp] = useState(null);
    const parentInfo = useSelector((state) => state.parentInfo);
    const parent_email = parentInfo.Email1 || parentInfo.par_email || getParentEmails(parentInfo.parentHistory).combined;

    let datePart;
    let timePart;
    let SessionID = location.state.SessionID;
    let parentID = location.state.parentID;
    let college = location.state.college;
    let famName = location.state.famName;
    let TotalResAmount = location.state.TotalResAmount;
    let TotalDepFees = location.state.TotalDepFees;
    let TotalSchoolFeeswodisc = location.state.TotalSchoolFeeswodisc;
    let TotalDiscount = location.state.TotalDiscount;
    let TotalFeesWithDiscount = location.state.TotalFeesWithDiscount
    let PayableAmount = location.state.PayableAmount
    let CustomAmount = location.state.CustomAmount
    let RemainingAmount = location.state.RemainingAmount
    let AmountPaidFromAICCredit = location.state.AmountPaidFromAICCredit;
    let AmountPaidFromBpoint = location.state.AmountPaidFromBpoint;
    let TransactionResponse = location.state.TransactionResponse;
    let TransactionDateTime = location.state.TransactionDateTime;
    let ReceiptNumber = location.state.ReceiptNumber;
    let BillerCode = 0;
    let MerchantNumber = location.state.MerchantNumber;

    const dateTime = new Date(TransactionDateTime);

    const year = dateTime.getFullYear();
    const month = dateTime.getMonth() + 1;
    const day = dateTime.getDate();

    const hours = dateTime.getHours();
    const minutes = dateTime.getMinutes();
    const seconds = dateTime.getSeconds();
    datePart = `${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}-${year}`;
    timePart = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    const updateTransactioninDB = async (SessionID, parentID, college, famName, TotalResAmount, TotalDepFees, TotalSchoolFeeswodisc, TotalDiscount, TotalFeesWithDiscount, PayableAmount, CustomAmount, RemainingAmount, AmountPaidFromAICCredit, AmountPaidFromBpoint, TransactionResponse, TransactionDateTime, ReceiptNumber, BillerCode, MerchantNumber, datePart, timePart) => {

        const commonData = {
            SessionID: SessionID,
            parentID: parentID,
            college: college,
            famName: famName,
            TotalResAmount: TotalResAmount,
            TotalDepFees: TotalDepFees,
            TotalSchoolFeeswodisc: TotalSchoolFeeswodisc,
            TotalDiscount: TotalDiscount,
            TotalFeesWithDiscount: TotalFeesWithDiscount,
            PayableAmount: PayableAmount,
            CustomAmount: CustomAmount,
            RemainingAmount: RemainingAmount,
            AmountPaidFromAICCredit: AmountPaidFromAICCredit,
            AmountPaidFromBpoint: AmountPaidFromBpoint,
            TransactionResponse: TransactionResponse,
            TransactionDateTime: TransactionDateTime,
            ReceiptNumber: ReceiptNumber,
            BillerCode: BillerCode,
            MerchantNumber: MerchantNumber,
            datePart: datePart,
            timePart: timePart,
        }

        // Run both API calls concurrently using Promise.all
        let [result, paylogs] = await Promise.all([
            URL2.post('/updateTransactioninpaydetails', commonData),
            URL2.post('/parent/parent-pay-detail-logs', commonData)
        ]);

        return { result, paylogs };
    };

    const persistCampusPayment = async (charge, txnOverride = {}) => {
        const campusSessionID = charge.SessionID || SessionID;
        const campusParentID = charge.parent_code || parentID;
        const campusCollege = charge.college || college;
        const campusCustom = charge.CustomAmount ?? CustomAmount;
        const campusRes = charge.TotalResAmount ?? TotalResAmount;
        const campusDep = charge.TotalDepFees ?? TotalDepFees;

        await updateTransactioninDB(
            campusSessionID,
            campusParentID,
            campusCollege,
            famName,
            campusRes,
            campusDep,
            charge.TotalSchoolFeeswodisc ?? TotalSchoolFeeswodisc,
            charge.TotalDiscount ?? TotalDiscount,
            charge.TotalFeesWithDiscount ?? TotalFeesWithDiscount,
            charge.PayableAmount ?? charge.payableAmount ?? PayableAmount,
            campusCustom,
            charge.RemainingAmount ?? RemainingAmount,
            charge.AmountPaidFromAICCredit ?? AmountPaidFromAICCredit,
            charge.AmountPaidFromBpoint ?? AmountPaidFromBpoint,
            txnOverride.TransactionResponse ?? TransactionResponse,
            txnOverride.TransactionDateTime ?? TransactionDateTime,
            txnOverride.ReceiptNumber ?? ReceiptNumber,
            txnOverride.BillerCode ?? BillerCode,
            txnOverride.MerchantNumber ?? MerchantNumber,
            datePart,
            timePart
        );
        await updatePaymentdataForSession(campusSessionID, campusRes, campusDep, campusCustom);
        await generateFeereceipt(campusSessionID, campusParentID);
        // The payment is saved by now. A confirmation email that cannot be queued is not a failed
        // save: 19892 was told "We could not finish saving it" because the email step answered 400.
        try {
            await sendEmail(campusSessionID, campusParentID, campusCollege);
        } catch (error) {
            console.error('Payment saved, but the confirmation email could not be queued:', error);
        }
    };

    const updatePaymentdataForSession = async (sessionId, TotalResAmount, TotalDepFees, CustomAmount) => {
        return URL2.post('/updatepaymentdata', {
            SessionID: sessionId,
            TotalResAmount: TotalResAmount,
            TotalDepFees: TotalDepFees,
            CustomAmount: CustomAmount,
        });
    }

    const updatePaymentdata = async (TotalResAmount, TotalDepFees, CustomAmount) => {
        return updatePaymentdataForSession(SessionID, TotalResAmount, TotalDepFees, CustomAmount);
    }

    const generateFeereceipt = async (SessionID, parentID) => {
        let generateFeereceiptResult = await URL2.post('/generateFeereceipt', {
            SessionID: SessionID,
            ParentID: parentID
        });
        return generateFeereceiptResult;
    }
    const sendEmail = async (sessionId, emailParentId, emailCollege) => {
        return queueConfirmationEmail({
            SessionID: sessionId,
            parent_email,
            family_name: famName,
            college: emailCollege || college,
            parent_id: emailParentId || parentID,
        });
    }

    useEffect(() => {
        if (localStorage.getItem("LoginToken") === null) {
            navigate('/');
        }
        else {
            readStatusWithRetries(checkStatus)
                .then((data) => {
                    // Still unreadable after three tries: say so and offer to try again, rather than
                    // leave the spinner up. Nothing is saved without the status, because it is what
                    // stops a payment being recorded (and emailed, and logged) twice.
                    if (!data) {
                        setSaveError('status');
                        return;
                    }
                    if (data && data.RenrolStatus !== undefined) {

                        // Family status can already read Completed while another
                        // campus payment is outstanding, so the campus that just
                        // paid still needs to be recorded.
                        if (!shouldRedirectToCompletion(data)) {
                            if (TransactionDateTime !== '') {
                                const dateTime = new Date(TransactionDateTime);

                                const year = dateTime.getFullYear();
                                const month = dateTime.getMonth() + 1;
                                const day = dateTime.getDate();

                                const hours = dateTime.getHours();
                                const minutes = dateTime.getMinutes();
                                const seconds = dateTime.getSeconds();

                                datePart = `${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}-${year}`;
                                timePart = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                            }

                            const afterfetchTransactionResult = async () => {
                                if (hasSaved.current) return;
                                if (SessionID !== '' && parentID !== '' && college !== '' && famName !== '' && TotalResAmount !== '' && TotalDepFees !== '' && TotalSchoolFeeswodisc !== '' && TotalDiscount !== '' && TotalFeesWithDiscount !== '' && PayableAmount !== '' && CustomAmount !== '' && RemainingAmount !== '' && AmountPaidFromAICCredit !== '' && AmountPaidFromBpoint !== '' && TransactionResponse !== '') {
                                    hasSaved.current = true;
                                    const campusCharges = getCampusPaymentCharges().filter((c) => c && c.SessionID);
                                    const paidIds = new Set(
                                        (data.paidCampuses || data.campusStatuses || [])
                                            .filter((c) => c.paid || ['Completed', 'Synced'].includes(c.status))
                                            .map((c) => String(c.SessionID))
                                    );

                                    // Only the campus this card payment actually paid for, plus any
                                    // campus settled from credit.
                                    //
                                    // This used to persist EVERY unpaid campus after a single card
                                    // payment, so one BPoint receipt was written against each of a
                                    // family's campuses. Four families on 2 and 5 October ended up
                                    // with Kewdale and Thornlie rows sharing a receipt, and because
                                    // the charge itself was the family total through one campus's
                                    // merchant, $9,310 of Thornlie's money sat in Kewdale's account
                                    // with no Thornlie transaction to receipt.
                                    //
                                    // A card charge belongs to one campus: the one named in the
                                    // redirect we came back on. Credit is different — it is not a
                                    // BPoint transaction and several campuses can be settled from it
                                    // at once, so those are still included.
                                    const chargedSession = String(SessionID);
                                    const toPersist = campusCharges.length
                                        ? campusCharges.filter((c) =>
                                            c.paymentStatus !== 'paid'
                                            && !paidIds.has(String(c.SessionID))
                                            && (Number(c.AmountPaidFromBpoint) <= 0
                                                || String(c.SessionID) === chargedSession)
                                        )
                                        : [{
                                            SessionID,
                                            parent_code: parentID,
                                            college,
                                            TotalResAmount,
                                            TotalDepFees,
                                            TotalSchoolFeeswodisc,
                                            TotalDiscount,
                                            TotalFeesWithDiscount,
                                            PayableAmount,
                                            CustomAmount,
                                            RemainingAmount,
                                            AmountPaidFromAICCredit,
                                            AmountPaidFromBpoint,
                                        }];

                                    for (const charge of toPersist) {
                                        const isCreditOnly = Number(charge.AmountPaidFromBpoint) <= 0;
                                        await persistCampusPayment(charge, isCreditOnly ? {
                                            TransactionResponse: 'Paid from AIC Credit',
                                            ReceiptNumber: 0,
                                            BillerCode: 0,
                                            MerchantNumber: 0,
                                        } : {});
                                        markCampusChargePaid(charge.SessionID);
                                    }

                                    const familyStatus = await checkStatus({
                                        campusSessions: getCampusSessions(),
                                    });

                                    // Another campus still to pay: say so, and keep everything needed
                                    // to pay it. Going on to the completed screen here is exactly the
                                    // "you are done" a parent must not be told while a campus is unpaid.
                                    const steps = cardPaymentSteps(getCampusPaymentCharges());
                                    let remaining = stepsStillToPay(steps);
                                    if (!remaining.length && hasOutstandingCampusPayment(familyStatus)) {
                                        remaining = getCampusPaymentSummary(familyStatus).unpaidCampuses.map((campus) => ({
                                            SessionID: campus.SessionID,
                                            college: campus.college || campus.campus_name || 'another campus',
                                            amount: 0,
                                        }));
                                    }
                                    if (remaining.length) {
                                        const charged = steps.find((step) => String(step.SessionID) === chargedSession);
                                        setNextUp({
                                            paid: {
                                                college: charged?.college || college,
                                                amount: charged?.amount ?? AmountPaidFromBpoint,
                                                receipt: ReceiptNumber,
                                            },
                                            steps,
                                            remaining,
                                        });
                                        return;
                                    }

                                    clearCampusPaymentCharges();
                                    clearPaymentResumeState();
                                    navigate('/reenrolmentcompleted', {
                                        replace: true,
                                        state: {
                                            t_date: familyStatus?.t_date,
                                            t_time: familyStatus?.t_time,
                                            campusStatuses: familyStatus?.campusStatuses || [],
                                        },
                                    });
                                    return;
                                }
                            }
                            afterfetchTransactionResult().catch((error) => {
                                hasSaved.current = false;
                                console.error('Unable to save campus payment:', error);
                                setSaveError('save');
                            });
                        }

                        else {
                            navigate('/reenrolmentcompleted', {
                                state: {
                                    t_date: data.t_date,
                                    t_time: data.t_time,
                                    campusStatuses: data.campusStatuses || [],
                                }
                            });
                        }
                    }
                })
                .catch((error) => {
                    console.error("An error occurred:", error);
                    setSaveError('save');
                });
        }

    }, [parentID, datePart, timePart, college, BillerCode, ReceiptNumber, MerchantNumber]);


    // One screen after paying, not two: this one only saves the payment and moves on to the
    // completed screen, which shows the payment itself. It speaks only if the save went wrong — and
    // then gives the receipt to quote, because the card has been charged either way.
    // Leaving with a campus unpaid asks first. Only here: on the card page BPoint itself navigates
    // away when a payment goes through, and must not be interrupted.
    useEffect(() => {
        if (!nextUp) return undefined;
        const warn = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [nextUp]);

    const content = nextUp ? (
        <CampusPaymentDone
            paid={nextUp.paid}
            steps={nextUp.steps}
            remaining={nextUp.remaining}
            onContinue={() => navigate('/bpointpaymentform', { replace: true, state: getPaymentResumeState() })}
        />
    ) : saveError === 'status' ? (
        <Box sx={{ maxWidth: 640, mx: 'auto', my: 4, textAlign: 'center' }} data-testid="status-unreadable">
            <Heading text={'Your payment went through'} />
            <Typography sx={{ color: '#475569', mt: 1 }}>
                {`We could not reach our system to finish recording it just now. Please do not pay again${ReceiptNumber ? ` — your receipt is ${ReceiptNumber}` : ''}.`}
            </Typography>
            <Button variant="contained" onClick={() => window.location.reload()} sx={{ mt: 2, textTransform: 'none', fontWeight: 700 }}>
                Try again
            </Button>
        </Box>
    ) : saveError ? (
        <Box sx={{ maxWidth: 640, mx: 'auto', my: 4, textAlign: 'center' }}>
            <Heading text={'Your payment went through'} />
            <Typography sx={{ color: '#475569', mt: 1 }}>
                {`We could not finish saving it just now. Please contact the school's Accounts Receivable team and quote receipt ${ReceiptNumber || 'from your bank statement'} — you do not need to pay again.`}
            </Typography>
        </Box>
    ) : (
        <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress />
            <Typography sx={{ mt: 2, color: '#475569' }}>Saving your payment…</Typography>
        </Box>
    );

    return (
        <>
            <MainTemplate content={content} SessionID={SessionID} college={college} />
        </>
    )
}

export default Thankyou;